import { Dialog } from "@/components/ui/Dialog"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/Button"
import { useAtomValue } from "jotai"
import { preClusteringProgressAtom } from "@/store/style/apply-style"

export interface ClusteringProgressDialogProps {
	onSkip: () => void
}

export const ClusteringProgressDialog = (props: ClusteringProgressDialogProps) => {
	const { t } = useTranslation("components")
	const { onSkip } = props
	// Read progress from atom to avoid re-mounting dialog on each update
	const progress = useAtomValue(preClusteringProgressAtom)
	const clusteredCount = progress?.clusteredCount ?? 0
	const totalCount = progress?.totalCount ?? 0
	const progressPercent = totalCount > 0 ? Math.round((clusteredCount / totalCount) * 100) : 0

	return (
		<Dialog trackingViewName="clustering_progress_dialog" disableClose>
			<div className="flex w-[462px] flex-col space-y-4">
				<div className="flex flex-row items-center justify-between">
					<div className="text-md font-bold">
						{t("clusteringProgressDialog.preparingPhotos")}
					</div>
				</div>

				<div className="text-sm text-base-300">
					{t("clusteringProgressDialog.analyzingBackground")}
				</div>

				{/* Progress bar */}
				<div className="flex flex-col space-y-2">
					<div className="flex flex-row items-center justify-between text-xs text-base-400">
						<span>{t("clusteringProgressDialog.progress")}</span>
						<span>
							{t("clusteringProgressDialog.progressText", {
								clusteredCount,
								totalCount,
								progressPercent,
							})}
						</span>
					</div>
					<div
						role="progressbar"
						aria-valuenow={progressPercent}
						aria-valuemin={0}
						aria-valuemax={100}
						aria-label={t("clusteringProgressDialog.ariaClusteringProgress")}
						className="h-2 w-full overflow-hidden rounded-full bg-base-700"
					>
						<div
							className="h-full bg-accent-600 transition-all duration-300"
							style={{ width: `${progressPercent}%` }}
						/>
					</div>
				</div>

				<div className="flex flex-row justify-end">
					<Button
						trackingLabel="skip_clustering"
						aria-label={t("clusteringProgressDialog.ariaSkipPhotoClustering")}
						onClick={onSkip}
					>
						{t("clusteringProgressDialog.continueInBackground")}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
